import { useSelector } from "react-redux";
import React from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ErrorPage from "../components/ui/Error/ErrorPage";
import Loading from "../components/ui/Loading/Loading";
import IsLoggedIn from "./IsLoggedIn";
import NotLoggedIn from "./NotLoggedIn";
import pagesData from "./pagesData";
import { AdminLayout } from "../pages/admin";
import { adminRoutes } from "../pages/admin";
import { platformRoutes } from "../pages/platform";
import SitePage from "../pages/site";
import TenantPreviewBoundary from "./TenantPreviewBoundary";
import {
  buildTenantRelativePath,
  isCustomTenantHost,
} from "../utils/tenantPreview";
import { get } from "../services/api";
import { usePublicConfig } from "../hooks/usePublicConfig";

const GetStartedPage = React.lazy(() => import("../pages/get-started"));
const VerifyPage = React.lazy(() => import("../pages/onboarding/VerifyPage"));
const OnboardingChatPage = React.lazy(() => import("../pages/onboarding/chat"));
const ClanRedesignPage = React.lazy(() => import("../pages/clanRedesign"));
const CommercePage = React.lazy(() => import("../pages/commerce"));
const GoalDetailPage = React.lazy(() => import("../pages/goals/detail"));
const GoalConfidenceDashboardPage = React.lazy(() =>
  import("../pages/goals/confidence-dashboard")
);
const RoleSignupPage = React.lazy(() => import("../pages/roles/RoleSignup"));
const WorkspaceSection = React.lazy(() => import("../pages/workspaceSection"));
const AuditLogPage = React.lazy(() => import("../pages/admin/audit-log"));
const RolesAdminPage = React.lazy(() => import("../pages/admin/roles"));
const CronHealthPage = React.lazy(() => import("../pages/admin/cron-health"));
const ReportsAdminPage = React.lazy(() => import("../pages/admin/reports"));
const InviteAcceptPage = React.lazy(() => import("../pages/inviteAccept"));
const JoinByCodePage = React.lazy(() => import("../pages/joinByCode"));

// Calibration fold-in (.agent/clan-admin-parity-execplan.md §2 point 3:
// "Calibration is folded into the shell"). Nests the same four calibration
// pages (Roles/Reports/AuditLog/CronHealth) under AdminLayout's own
// <Outlet> at /administrator/calibration/*. Stages 1-3 built this out,
// stripped each page's own duplicate header, and retargeted AdminLayout's
// Governance nav item + the tenant-admin allowlist at this destination.
// Stage 4 redirects every legacy flat /admin/* path here (see
// LegacyCalibrationRedirect below) and retires ClanAdminUtilityFrame /
// ClanLegacyPageShell, the wrapper those legacy routes used to render
// through — nothing renders through that shell anymore.
//
// Every entry needs its own <Suspense>: unlike the legacy routes (which
// inherited IsLoggedIn's <Suspense>, IsLoggedIn.js), AdminLayout's <Outlet>
// (pages/admin/AdminLayout.jsx) has no ambient Suspense boundary of its own
// — same reason pages/admin/index.jsx wraps every adminRoutes entry in its
// own local AdminSuspense.
const calibrationFoldInRoutes = (
  <>
    <Route path="calibration" element={<Navigate to="roles" replace />} />
    <Route
      path="calibration/roles"
      element={
        <React.Suspense fallback={<Loading />}>
          <RolesAdminPage />
        </React.Suspense>
      }
    />
    <Route
      path="calibration/reports"
      element={
        <React.Suspense fallback={<Loading />}>
          <ReportsAdminPage />
        </React.Suspense>
      }
    />
    <Route
      path="calibration/audit-log"
      element={
        <React.Suspense fallback={<Loading />}>
          <AuditLogPage />
        </React.Suspense>
      }
    />
    <Route
      path="calibration/cron-health"
      element={
        <React.Suspense fallback={<Loading />}>
          <CronHealthPage />
        </React.Suspense>
      }
    />
  </>
);

function PublicCommerceBoundary() {
  return (
    <React.Suspense fallback={<Loading />}>
      <Outlet />
    </React.Suspense>
  );
}

function TenantEntryRoute({ user }) {
  const { tenantSlug = "", boardSlug = "" } = useParams();
  const explicitBoardRoute = Boolean(`${boardSlug || ""}`.trim());
  const authIdentity = `${
    user?.userinfo?._id ||
    user?.userinfo?.guid ||
    user?.userinfo?.username ||
    user?.userinfo?.email ||
    ""
  }`.trim();
  const { data: landingResponse, isLoading: landingLoading } = useQuery({
    queryKey: [
      "workspace-landing",
      tenantSlug || "custom-domain",
      authIdentity || "anonymous",
    ],
    queryFn: () =>
      get(buildTenantRelativePath("/api/v1/workspace/landing", tenantSlug)),
    enabled: !explicitBoardRoute && Boolean(authIdentity),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const landingEnvelope = landingResponse?.data || landingResponse || {};
  const landing =
    landingEnvelope?.data && typeof landingEnvelope.data === "object"
      ? landingEnvelope.data
      : landingEnvelope;
  const subclanGuid = `${
    landing?.subclanGuid ||
    landing?.subclanId ||
    landing?.subclan?.guid ||
    landing?.workspace?.guid ||
    landing?.target?.guid ||
    (landing?.kind === "subclan" ? landing?.guid : "") ||
    ""
  }`.trim();
  const landingWorkspaceKind = `${
    landing?.workspaceKind ||
    landing?.kind ||
    landing?.scope ||
    landing?.workspace?.kind ||
    landing?.target?.kind ||
    ""
  }`
    .trim()
    .toLowerCase();
  const subclanLandingPath =
    !explicitBoardRoute &&
    subclanGuid &&
    (!landingWorkspaceKind || landingWorkspaceKind === "subclan")
      ? buildTenantRelativePath(
          `/subclan/${encodeURIComponent(subclanGuid)}`,
          tenantSlug
        )
      : "";

  return (
    <IsLoggedIn user={user}>
      {!explicitBoardRoute && landingLoading ? (
        <Loading />
      ) : subclanLandingPath ? (
        <Navigate to={subclanLandingPath} replace />
      ) : (
        <ClanRedesignPage surface="home" />
      )}
    </IsLoggedIn>
  );
}

/* T-062 S4 — every `/subclan/<id>/<tab>` URL renders the subclan workspace,
 * including a tab the workspace does not have.
 *
 * This route used to hold an allowlist and answer anything outside it with a
 * silent `<Navigate replace>` back to the subclan home. That swallows the URL
 * the person (or an automation run) asked for without a word, which is the
 * first defect named in AGENTS.md's Automation-First UI Contract, and it left
 * no way to tell a mistyped tab from one that exists. HomeSurface owns the
 * answer now: it maps the tab to a pane, and renders
 * `clan-redesign-subclan-tab-not-found` in place for one it does not know.
 * The pane map (REDESIGN_SUBCLAN_TAB_VIEW_MAP in pages/clanRedesign/index.jsx)
 * is the single list of a subclan's tabs; there is no second copy here to
 * drift from it.
 */
function TenantSubclanRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="home" workspaceKind="subclan" />
    </IsLoggedIn>
  );
}

function TenantRolesRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="roles" />
    </IsLoggedIn>
  );
}

function TenantGoalsRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="goals" />
    </IsLoggedIn>
  );
}

function TenantGoalDetailRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <GoalDetailPage />
    </IsLoggedIn>
  );
}

function TenantGoalConfidenceDashboardRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <GoalConfidenceDashboardPage />
    </IsLoggedIn>
  );
}

function TenantWorkspacePaneRoute({ user, pane }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface={pane} />
    </IsLoggedIn>
  );
}

function TenantReportsRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="reports" />
    </IsLoggedIn>
  );
}

function TenantCommerceRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <CommercePage />
    </IsLoggedIn>
  );
}

// Clan Admin harvest, Stage 4/5 (.agent/clan-admin-parity-execplan.md §0;
// <SCRATCH>/reports/N10-adminsurface-harvest.md and
// W7-adminsurface-retire.md). AdminSurface (the old ClanRedesignPage
// surface="admin") hand-duplicated /administrator. Its two genuinely
// prototype-correct gaps — the people rail and its bundled invite FAB —
// were ported into AdminLayout.jsx directly (components/shared/
// WorkspacePeopleRail.jsx), after which nothing on the old surface was
// worth keeping (its sparklines were decorative, its floating rail card
// and weekly-chart-default were already matched or fixed elsewhere, and
// its "Signed in as" footer read a field that was always undefined — a
// bug, not a feature). Bare /admin and /:tenantSlug/admin now just
// redirect to the canonical console so bookmarked/shared URLs keep
// working, mirroring TenantLegacyHomeRedirect/LegacyCalibrationRedirect
// below. No IsLoggedIn wrapper needed: AdminLayout enforces its own auth
// check on the destination.
function TenantAdminCanonicalRedirect() {
  const { tenantSlug = "" } = useParams();
  const nextPath = buildTenantRelativePath("/administrator", tenantSlug);

  return <Navigate to={nextPath} replace />;
}

function TenantFlowsRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="flows" />
    </IsLoggedIn>
  );
}

function TenantSearchRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="search" />
    </IsLoggedIn>
  );
}

function TenantSubclansRoute({ user }) {
  return (
    <IsLoggedIn user={user}>
      <ClanRedesignPage surface="subclans" />
    </IsLoggedIn>
  );
}

// Calibration fold-in, Stage 4 (.agent/clan-admin-parity-execplan.md §2
// point 3 / the E-calibration-foldin migration plan). Roles/Reports/AI
// audit log/Health used to render directly at these legacy paths, wrapped
// in the now-retired ClanAdminUtilityFrame/ClanLegacyPageShell. They now
// live exclusively at /administrator/calibration/<tab>
// (calibrationFoldInRoutes above, nested under AdminLayout's own Outlet) —
// this just redirects any bookmarked legacy URL there, preserving the
// tenant slug (buildTenantRelativePath resolves to a bare path on a custom
// tenant domain, same as TenantLegacyHomeRedirect/LegacyFinanceRedirect
// below). No IsLoggedIn wrapper needed: AdminLayout enforces its own auth
// check on the destination.
//
// Bare /admin and /:tenantSlug/admin are NOT part of this family — they
// are TenantAdminCanonicalRedirect's concern (above), a separate redirect
// to a different destination (/administrator itself, not a calibration
// tab) that happens to share this same replace-in-place pattern.
function LegacyCalibrationRedirect({ tab }) {
  const { tenantSlug = "" } = useParams();
  const nextPath = buildTenantRelativePath(
    `/administrator/calibration/${tab}`,
    tenantSlug
  );

  return <Navigate to={nextPath} replace />;
}

function CustomDomainOnly({ children }) {
  return isCustomTenantHost() ? children : <ErrorPage />;
}

function TenantLegacyHomeRedirect({ user }) {
  const { tenantSlug = "" } = useParams();
  const nextPath = buildTenantRelativePath("/", tenantSlug);

  return <Navigate to={nextPath} replace />;
}

function LegacyFinanceRedirect() {
  const {
    tenantSlug = "",
    commerceSection = "",
    financeSection = "",
  } = useParams();
  const legacySection = `${commerceSection || financeSection || ""}`.trim().toLowerCase();
  const legacySectionAliases = {
    billing: "orders",
    bills: "orders",
    invoices: "orders",
    invoice: "orders",
    checkout: "orders",
    payments: "orders",
    payment: "orders",
    subscriptions: "settings",
    subscription: "settings",
    plans: "settings",
    plan: "settings",
    wallet: "analytics",
    wallets: "analytics",
    revenue: "analytics",
    commerce: "home",
    home: "home",
  };
  const section = legacySectionAliases[legacySection] || legacySection;
  const nextPath = buildTenantRelativePath(
    section && section !== "home" ? `/commerce/${section}` : "/commerce",
    tenantSlug
  );

  return <Navigate to={nextPath} replace />;
}

const explicitTenantCommercePaths = new Set([
  "commerce",
  "commerce/:commerceSection",
]);

function RootHomeRedirect({ user }) {
  const authUser = user?.userinfo || null;
  const customTenantHost = isCustomTenantHost();
  const authIdentity =
    `${authUser?._id || authUser?.guid || authUser?.username || authUser?.email || authUser?.mobile || "anon"}`.trim() ||
    "anon";

  const {
    data: publicConfig,
    isLoading: isLoadingPublicConfig,
  } = usePublicConfig({
    enabled: Boolean(authUser) && customTenantHost,
    staleTime: 60 * 1000,
    retry: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["root-home-pending-checkout", authIdentity],
    queryFn: async () => {
      const response = await get("/api/v1/users/pending-checkout");
      return response || {};
    },
    enabled: Boolean(authUser),
    staleTime: 0,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  if (!authUser) {
    return <Navigate to="/login" replace />;
  }

  if (customTenantHost) {
    if (isLoadingPublicConfig) {
      return <Loading />;
    }
    if (publicConfig?.tenantContext?.resolvedBy === "domain") {
      return <TenantEntryRoute user={user} />;
    }
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Navigate
      to={data?.pending ? "/account?notice=payment-outstanding" : "/account"}
      replace
    />
  );
}

const Router = () => {
  const { user } = useSelector((state) => ({ ...state }));
  const tenantOnlyPages = [];

  const buildPageRoutes = (items, prefix = "") =>
    items.map(({ path, title, element }) => {
      const fullPath = prefix
        ? path
          ? `${prefix}/${path}`
          : prefix
        : `/${path}`;

      return <Route key={`${prefix || "root"}:${title}:${path}`} path={fullPath} element={element} exact />;
    });

  const tenantOnlyRoutes = tenantOnlyPages.map(({ path, title, section }) => (
    <Route
      key={`tenant-only:${title}:${path}`}
      path={path}
      element={<WorkspaceSection section={section} />}
      exact
    />
  ));

  const publicPages = pagesData.filter(({ priv }) => !priv);
  const privatePages = pagesData.filter(({ priv }) => priv);
  const tenantScopedPublicPages = publicPages.filter(({ path }) => path !== "");
  const rootPrivatePages = privatePages.filter(
    ({ path }) =>
      !explicitTenantCommercePaths.has(path) &&
      path !== "home" &&
      path !== "subclan/:guid" &&
      path !== "subclan/:guid/:tab"
  );
  const tenantScopedPrivatePages = privatePages.filter(
    ({ path }) =>
      !explicitTenantCommercePaths.has(path) &&
      path !== "home" &&
      path !== "subclan/:guid" &&
      path !== "subclan/:guid/:tab"
  );
  const rootPageRoutes = buildPageRoutes(publicPages);
  const rootPrivateRoutes = buildPageRoutes(rootPrivatePages);
  const tenantPublicRoutes = buildPageRoutes(tenantScopedPublicPages, "/:tenantSlug");
  const tenantPrivateRoutes = buildPageRoutes(tenantScopedPrivatePages, "/:tenantSlug");

  return (
    <Routes>
      <Route path="app">{platformRoutes}</Route>

      <Route element={<TenantPreviewBoundary />}>
        <Route element={<PublicCommerceBoundary />}>
          <Route path="onboarding/verify" element={<VerifyPage />} />
          <Route path="onboarding/chat" element={<OnboardingChatPage />} />
          <Route path="get-started/verify" element={<VerifyPage />} />
          <Route path="get-started" element={<GetStartedPage />} />
          <Route path="get-started/:planSlug" element={<GetStartedPage />} />
          <Route path="site/:slug" element={<SitePage />} />
          {/* GitHub-style invite accept — public (works logged-out) AND
              functional logged-in; the page branches on auth state and
              auto-accepts after the user returns from login/signup. No
              IsLoggedIn/NotLoggedIn guard here on purpose. */}
          <Route path="invite/:token" element={<InviteAcceptPage />} />
          <Route path=":tenantSlug/invite/:token" element={<InviteAcceptPage />} />
          {/* General (reusable) invite links — public; branches on auth like
              the per-recipient page above. Subclan codes join clan + subclan. */}
          <Route path="join/:code" element={<JoinByCodePage />} />
          <Route path=":tenantSlug/join/:code" element={<JoinByCodePage />} />
        </Route>

        {/* Admin panel - separate layout, own auth check inside AdminLayout */}
        <Route path="administrator" element={<AdminLayout />}>
          {adminRoutes}
          {calibrationFoldInRoutes}
        </Route>

        <Route path="home" element={<RootHomeRedirect user={user} />} />
        <Route
          path="join/:roleKey"
          element={
            <CustomDomainOnly>
              <RoleSignupPage />
            </CustomDomainOnly>
          }
        />
        <Route
          path="goals"
          element={
            <CustomDomainOnly>
              <TenantGoalsRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="search"
          element={
            <CustomDomainOnly>
              <TenantSearchRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclans"
          element={
            <CustomDomainOnly>
              <TenantSubclansRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclans/all"
          element={
            <CustomDomainOnly>
              <TenantSubclansRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclans/my"
          element={
            <CustomDomainOnly>
              <TenantSubclansRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="goals/dashboard/:goalId"
          element={
            <CustomDomainOnly>
              <TenantGoalConfidenceDashboardRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="roles"
          element={
            <CustomDomainOnly>
              <TenantRolesRoute user={user} />
            </CustomDomainOnly>
          }
        />
        {["notes", "calendar", "events", "alerts", "notifications", "connections", "bonds", "messages", "conversations"].map((pane) => (
          <Route
            key={`custom-${pane}`}
            path={pane}
            element={
              <CustomDomainOnly>
                <TenantWorkspacePaneRoute user={user} pane={pane} />
              </CustomDomainOnly>
            }
          />
        ))}
        <Route
          path="commerce"
          element={
            <CustomDomainOnly>
              <TenantCommerceRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="commerce/:commerceSection"
          element={
            <CustomDomainOnly>
              <TenantCommerceRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="finance"
          element={
            <CustomDomainOnly>
              <LegacyFinanceRedirect />
            </CustomDomainOnly>
          }
        />
        <Route
          path="finance/:financeSection"
          element={
            <CustomDomainOnly>
              <LegacyFinanceRedirect />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclan/:guid/boards/:boardSlug"
          element={
            <CustomDomainOnly>
              <TenantSubclanRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclan/:guid"
          element={
            <CustomDomainOnly>
              <TenantSubclanRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="subclan/:guid/:tab"
          element={
            <CustomDomainOnly>
              <TenantSubclanRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route
          path="boards/:boardSlug"
          element={
            <CustomDomainOnly>
              <TenantEntryRoute user={user} />
            </CustomDomainOnly>
          }
        />
        <Route path=":tenantSlug/join/:roleKey" element={<RoleSignupPage />} />
        <Route path=":tenantSlug" element={<TenantEntryRoute user={user} />} />
        <Route path=":tenantSlug/search" element={<TenantSearchRoute user={user} />} />
        <Route path=":tenantSlug/goals" element={<TenantGoalsRoute user={user} />} />
        <Route
          path=":tenantSlug/goals/dashboard/:goalId"
          element={<TenantGoalConfidenceDashboardRoute user={user} />}
        />
        <Route path=":tenantSlug/goals/:goalId" element={<TenantGoalDetailRoute user={user} />} />
        {/* Onboarding chat is intentionally full-bleed — the user is mid-
            setup and the workspace shell (topbar/footer/chat sidebar)
            distracts from the conversation. Auth-only wrapper. */}
        <Route
          path=":tenantSlug/onboarding/chat"
          element={
            user ? (
              <React.Suspense fallback={<Loading />}>
                <OnboardingChatPage />
              </React.Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path=":tenantSlug/roles" element={<TenantRolesRoute user={user} />} />
        {["notes", "calendar", "events", "alerts", "notifications", "connections", "bonds", "messages", "conversations"].map((pane) => (
          <Route
            key={`tenant-${pane}`}
            path={`:tenantSlug/${pane}`}
            element={<TenantWorkspacePaneRoute user={user} pane={pane} />}
          />
        ))}
        <Route path=":tenantSlug/commerce" element={<TenantCommerceRoute user={user} />} />
        <Route path=":tenantSlug/commerce/:commerceSection" element={<TenantCommerceRoute user={user} />} />
        <Route path=":tenantSlug/finance" element={<LegacyFinanceRedirect />} />
        <Route path=":tenantSlug/finance/:financeSection" element={<LegacyFinanceRedirect />} />
        <Route path=":tenantSlug/subclans" element={<TenantSubclansRoute user={user} />} />
        <Route path=":tenantSlug/subclans/all" element={<TenantSubclansRoute user={user} />} />
        <Route path=":tenantSlug/subclans/my" element={<TenantSubclansRoute user={user} />} />
        <Route path=":tenantSlug/reports" element={<TenantReportsRoute user={user} />} />
        <Route path=":tenantSlug/flows" element={<TenantFlowsRoute user={user} />} />
        <Route
          path=":tenantSlug/admin"
          element={<TenantAdminCanonicalRedirect />}
        />
        <Route
          path="admin"
          element={
            <CustomDomainOnly>
              <TenantAdminCanonicalRedirect />
            </CustomDomainOnly>
          }
        />
        <Route
          path=":tenantSlug/admin/audit-log"
          element={<LegacyCalibrationRedirect tab="audit-log" />}
        />
        <Route
          path="admin/audit-log"
          element={
            <CustomDomainOnly>
              <LegacyCalibrationRedirect tab="audit-log" />
            </CustomDomainOnly>
          }
        />
        <Route
          path=":tenantSlug/admin/roles"
          element={<LegacyCalibrationRedirect tab="roles" />}
        />
        <Route
          path="admin/roles"
          element={
            <CustomDomainOnly>
              <LegacyCalibrationRedirect tab="roles" />
            </CustomDomainOnly>
          }
        />
        <Route
          path=":tenantSlug/admin/cron-health"
          element={<LegacyCalibrationRedirect tab="cron-health" />}
        />
        <Route
          path="admin/cron-health"
          element={
            <CustomDomainOnly>
              <LegacyCalibrationRedirect tab="cron-health" />
            </CustomDomainOnly>
          }
        />
        <Route
          path=":tenantSlug/admin/reports"
          element={<LegacyCalibrationRedirect tab="reports" />}
        />
        <Route
          path="admin/reports"
          element={
            <CustomDomainOnly>
              <LegacyCalibrationRedirect tab="reports" />
            </CustomDomainOnly>
          }
        />
        <Route
          path=":tenantSlug/subclan/:guid"
          element={<TenantSubclanRoute user={user} />}
        />
        <Route
          path=":tenantSlug/subclan/:guid/:tab"
          element={<TenantSubclanRoute user={user} />}
        />
        <Route
          path=":tenantSlug/subclan/:guid/boards/:boardSlug"
          element={<TenantSubclanRoute user={user} />}
        />
        <Route
          path=":tenantSlug/boards/:boardSlug"
          element={<TenantEntryRoute user={user} />}
        />
        <Route
          path=":tenantSlug/home"
          element={<TenantLegacyHomeRedirect user={user} />}
        />
        <Route path=":tenantSlug/administrator" element={<AdminLayout />}>
          {adminRoutes}
          {calibrationFoldInRoutes}
        </Route>

        <Route element={<IsLoggedIn user={user} />}>{rootPrivateRoutes}</Route>
        <Route element={<IsLoggedIn user={user} />}>{tenantPrivateRoutes}</Route>
        <Route element={<IsLoggedIn user={user} />}>{tenantOnlyRoutes}</Route>
        <Route element={<NotLoggedIn user={user} />}>{rootPageRoutes}</Route>
        <Route element={<NotLoggedIn user={user} />}>{tenantPublicRoutes}</Route>

        <Route path=":tenantSlug/site/:slug" element={<SitePage />} />
      </Route>
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
};

export default Router;
